<template>
  <v-dialog content-class="loading-dialog" v-model="dialog" persistent width="440">
    <v-card class="loading-card" color="primary" dark>
      <div class="loading-header">
        <svg class="spinner" viewBox="0 0 52 52">
          <circle class="spinner-track" cx="26" cy="26" r="22" />
          <circle class="spinner-ring" cx="26" cy="26" r="22" />
        </svg>
        <h2 class="loading-title">
          {{ $t('LoadingScreen.titleMsg') }}
          <span class="dot">.</span><span class="dot delay-1">.</span><span class="dot delay-2">.</span>
        </h2>
      </div>

      <div class="loading-body">
        <div
          v-for="(message, index) in progressMessages"
          :key="`step-${index}`"
          class="step"
        >
          <v-icon v-if="index < progressMessages.length - 1" small class="step-icon done">mdi-check-circle</v-icon>
          <v-icon v-else-if="progressMessages.length > 0 && index === progressMessages.length - 1 && message === 'Done'" small class="step-icon done">mdi-check-circle</v-icon>
          <v-icon v-else small class="step-icon active">mdi-loading</v-icon>
          <span class="step-label">{{ message }}</span>
        </div>
      </div>

      <div class="loading-status">
        <span class="status-text">{{ currentStatus }}</span>
        <span class="status-percent">{{ Math.round(progressValue) }}%</span>
      </div>

      <v-progress-linear
        class="loading-progress"
        color="success"
        :value="progressValue"
        height="3"
        background-opacity="0.12"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { createNamespacedHelpers } from 'vuex'
import MessageUtil from './mixins/MessageUtil'

const app = createNamespacedHelpers('app')
const stations = createNamespacedHelpers('stations')
const navigation = createNamespacedHelpers('navigation')
const guidelines = createNamespacedHelpers('guidelines')
const authenticate = createNamespacedHelpers('authenticate')
const uiConfig = createNamespacedHelpers('uiconfig')
const dataState = createNamespacedHelpers('datastate')
const dataSources = createNamespacedHelpers('datasource')
const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
const datum = createNamespacedHelpers('datum')
const flags = createNamespacedHelpers('flags')
const plugin = createNamespacedHelpers('plugin')
const landingPageStaticContent = createNamespacedHelpers('landingPageStaticContent')

const TOTAL_PROGRESS = 11

export default {
  mixins: [MessageUtil],
  data() {
    return {
      dialog: false,
      progressMessages: [],
      progress: 0,
      currentStatus: 'Initializing...',
    }
  },
  computed: {
    ...authenticate.mapGetters(['hasContinuousData', 'hasSampleData', 'currentUserGroup', 'isAuthed']),
    progressValue() {
      return Math.min((this.progress + 1) / TOTAL_PROGRESS * 100, 100)
    },
  },
  watch: {
    isAuthed: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.dialog = true
          this.onLoading()
        }
      },
    },
    dialog(val) {
      if (!val) {
        this.resetLoading()
      }
    },
    progressMessages: {
      deep: true,
      handler(arr) {
        if (arr.length === 0) {
          this.currentStatus = 'Initializing...'
        } else {
          const last = arr[arr.length - 1]
          this.currentStatus = last === 'Done' ? 'Initialization complete' : last
        }
      },
    },
  },
  methods: {
    ...app.mapActions(['updateApplicationLoaded', 'loadingHasFinished']),
    ...guidelines.mapActions(['getGuidelines']),
    ...app.mapMutations(['SET_MapServiceConfig']),
    ...stations.mapMutations(['SET_MediaTypeList', 'SET_TagList', 'SET_StationCategories', 'SET_StationCategoriesContinuous']),
    ...navigation.mapActions(['fetchSideMenu', 'fetchRecentLocations']),
    ...monitoringFeatures.mapActions(['fetchMonitoringFeaturesUI']),
    ...uiConfig.mapMutations(['SetUiConfigOptions']),
    ...dataState.mapActions(['fetchUiConfigDataSuccess', 'fetchMediaTypeListSuccess',
      'fetchStationCategoryListSuccess', 'fetchContinuousStationCategoryListSuccess', 'fetchTagListSuccess',
      'fetchDiscreteStationSideMenuDataSuccess', 'fetchContinuousStationSideMenuDataSuccess',
      'fetchMapLayersSuccess', 'fetchLandingPageStaticContentTemplatesSuccess', 'fetchLandingPageStaticContentItemsSuccess',
    ]),
    ...dataSources.mapActions(['fetchDataSources']),
    ...datum.mapActions(['getDatumListForTenant']),
    ...flags.mapActions(['getFlagsByTenant']),
    ...plugin.mapActions(['getActivePlugins']),
    ...landingPageStaticContent.mapActions(['setStaticContentTemplates', 'setStaticContentItems']),

    populateDatumList() {
      return this.getDatumListForTenant({
        successCb: () => {},
        tenantName: this.currentUserGroup.name,
      })
    },

    async onLoading() {
      await Promise.all([
        this.fetchStartLoadingData(),
        this.fetchSideMenuLoadingData(),
        this.fetchDataSources(),
        this.populateDatumList(),
      ])

      if (this.currentUserGroup != null) {
        this.incrementProgress()
        if (this.hasContinuousData) {
          this.pushProgressMessage('Fetching time series stations')
        }

        this.incrementProgress()
        if (this.hasSampleData) {
          this.pushProgressMessage('Fetching grab sample stations')
        }
      }

      await this.onPostLoading()

      this.incrementProgress()
      this.pushProgressMessage('Done')
      this.updateApplicationLoaded(true)
      this.dialog = false
    },

    async onPostLoading() {
      this.loadingHasFinished()
      if (this.hasMessage) {
        this.outputMessageList()
      }
      await this.fetchGuidelines()
    },

    async fetchSideMenuLoadingData() {
      try {
        const [a, b] = [this.fetchSideMenu(), this.fetchMonitoringFeaturesUI()]
        await Promise.all([a, b])

        this.fetchDiscreteStationSideMenuDataSuccess()
        this.fetchContinuousStationSideMenuDataSuccess()
        this.incrementProgress()
      } catch (error) {
        this.setMessage(this.messageLevel.error, 'Failed to fetch sidemenu monitoring features',
          'Sorry, we couldn\'t load the side menu data. Please try again in a few minutes - let us know if it happens again')
        console.error(error)
        this.appendMessage(error)
      }
    },

    async fetchStartLoadingData() {
      try {
        const response = await axios.post('api/StartLoading', {
          TenantName: this.currentUserGroup.name,
        })

        const data  = response.data

        this.setLoadingData({
          payload: data.UiConfigOptions,
          success: (d) => { this.fetchUiConfigDataSuccess(); this.SetUiConfigOptions(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load UI configuration', error) },
        })

        this.setLoadingData({
          payload: data.MediaTypes,
          success: (d) => { this.fetchMediaTypeListSuccess(); this.SET_MediaTypeList(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load media types', error) },
        })

        this.setLoadingData({
          payload: data.StationCategories,
          success: (d) => { this.fetchStationCategoryListSuccess(); this.SET_StationCategories(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load discrete sample station categories', error) },
        })

        this.setLoadingData({
          payload: data.ContinuousStationCategories,
          success: (d) => { this.fetchContinuousStationCategoryListSuccess(); this.SET_StationCategoriesContinuous(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load continuous stations categories', error) },
        })

        this.setLoadingData({
          payload: data.Tags,
          success: (d) => { this.fetchTagListSuccess(); this.SET_TagList(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load tags list', error) },
        })

        this.setLoadingData({
          payload: data.MapLayers,
          success: (d) => { this.fetchMapLayersSuccess(); this.SET_MapServiceConfig(d && d.length ? d : this.$store.state.app.defaultMapConfig) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load map layers', error) },
        })

        this.setLoadingData({
          payload: data.LandingPageStaticContentTemplates,
          success: (d) => { this.fetchLandingPageStaticContentTemplatesSuccess(); this.setStaticContentTemplates(d) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load static content templates', error) },
        })

        this.setLoadingData({
          payload: data.LandingPageStaticContentItems,
          success: (d) => { this.fetchLandingPageStaticContentItemsSuccess(); this.setStaticContentItems({ items: d, forced: false }) },
          fail: (error) => { this.setMessage(this.messageLevel.error, 'System failed to load static content items', error) },
        })

        await Promise.all([
          this.getFlagsByTenant(this.currentUserGroup.id),
          this.getActivePlugins(),
        ])

        this.incrementProgress()
        this.pushProgressMessage('Fetching media types')

        this.incrementProgress()
        this.pushProgressMessage('Fetching station categories')

        this.incrementProgress()
        this.pushProgressMessage('Fetching continuous station categories')

        this.incrementProgress()
        this.pushProgressMessage('Fetching tags')

        this.incrementProgress()
        this.pushProgressMessage('Fetching map layers')

        this.incrementProgress()
        this.pushProgressMessage('Fetching flags')

        this.setMessage(this.messageLevel.success, 'Initialization successful', 'All data loaded.')
      } catch (error) {
        this.incrementProgress()
        this.appendMessage('start loading failed' + error)
      }
    },

    async fetchGuidelines() {
      try {
        await this.getGuidelines(this.currentUserGroup.id)
      } catch (error) {
        this.setMessage(this.messageLevel.error, 'Subject', error)
      }
    },

    pushProgressMessage(msg) {
      this.progressMessages.push(msg)
    },
    resetLoading() {
      this.progress = 0
      this.progressMessages = []
      this.currentStatus = 'Initializing...'
    },
    incrementProgress() {
      this.progress += 1
    },

    setLoadingData(options) {
      if (options.payload.IsSuccess) {
        options.success(options.payload.Data)
      } else {
        options.fail(options.payload.ErrorMessage)
      }
    },
  },

  mounted() {
    this.fetchRecentLocations()
  },
}
</script>

<style scoped>
.loading-dialog {
  z-index: 9999 !important;
}

.loading-card {
  background: linear-gradient(145deg, #333 0%, #3a3a3a 100%) !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.loading-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 8px;
}

.spinner {
  width: 52px;
  height: 52px;
  margin-bottom: 18px;
}

.spinner-track {
  fill: none;
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 3;
}

.spinner-ring {
  fill: none;
  stroke: #529943;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 138;
  stroke-dashoffset: 138;
  transform-origin: center;
  animation: spin 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes spin {
  0%   { stroke-dashoffset: 138; transform: rotate(0deg); }
  50%  { stroke-dashoffset: 28; transform: rotate(180deg); }
  100% { stroke-dashoffset: 138; transform: rotate(360deg); }
}

.loading-title {
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.3px;
  margin: 0;
}

.dot {
  opacity: 0;
  animation: dotPulse 1.4s ease-in-out infinite;
}

.delay-1 { animation-delay: 0.2s; }
.delay-2 { animation-delay: 0.4s; }

@keyframes dotPulse {
  0%, 30% { opacity: 0; }
  50% { opacity: 1; }
  70%, 100% { opacity: 0; }
}

.loading-body {
  padding: 12px 32px 20px;
  min-height: 120px;
}

.step {
  display: flex;
  align-items: center;
  padding: 4px 0;
  animation: fadeSlideIn 0.3s ease-out;
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}

.step-icon {
  margin-right: 10px;
  flex-shrink: 0;
}

.step-icon.done {
  color: #529943 !important;
}

.step-icon.active {
  color: #529943 !important;
  animation: iconPulse 1.6s ease-in-out infinite;
}

@keyframes iconPulse {
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
}

.step-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
}

.loading-progress {
  margin: 0 !important;
  border-radius: 0 !important;
}

.loading-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 32px 12px;
}

.status-text {
  font-size: 13px;
  color: #529943;
}

.status-percent {
  font-size: 14px;
  font-weight: 700;
  color: #529943;
  font-variant-numeric: tabular-nums;
}
</style>
